import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Waves, MessageCircle, LayoutDashboard, ClipboardList, Trophy, Wind, Thermometer, ArrowUp, Users, CalendarDays, MapPin, ChevronLeft, ChevronRight, Warehouse, Sun, X, Sparkles, Plane } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { BetaBadge } from '../components/PageShell';
import { useSubscription } from '../contexts/SubscriptionContext';
import VibeCheck from '../components/VibeCheck';
import StreakCard, { shouldShowMilestone } from '../components/StreakCard';
import StreakRescue from '../components/StreakRescue';
import NotificationsPanel from '../components/NotificationsPanel';
import BeachScene from '../components/BeachScene';
import { authFetch, API } from '../utils/api';


function SunglassesIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12c0-2 2-4 5-4h1.5a2 2 0 0 1 2 1.5c.2.8.8 1.5 1.5 1.5s1.3-.7 1.5-1.5A2 2 0 0 1 15.5 8H17c3 0 5 2 5 4s-2 4-5 4h-2c-1.5 0-2.5-1-3-2-.5 1-1.5 2-3 2H7c-3 0-5-2-5-4z" />
    </svg>
  );
}

function ThemeToggle({ themeMode, setThemeMode }) {
  const modes = [
    { key: 'light', label: 'Light', icon: <Sun className="w-3 h-3" /> },
    { key: 'dynamic', label: 'Auto', icon: <Waves className="w-3 h-3" /> },
    { key: 'dark', label: 'Dark', icon: <SunglassesIcon className="w-3.5 h-3.5" /> },
  ];
  const activeIdx = modes.findIndex(m => m.key === themeMode);

  return (
    <div
      className="relative flex items-center bg-white/5 border border-white/10 rounded-full p-0.5"
      data-testid="theme-mode-toggle"
    >
      {/* Sliding pill indicator */}
      <motion.div
        className="absolute top-0.5 bottom-0.5 rounded-full bg-primary/20 border border-primary/30"
        initial={false}
        animate={{ left: `calc(${activeIdx * 33.33}% + 2px)`, width: 'calc(33.33% - 4px)' }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      />
      {modes.map((m) => (
        <button
          key={m.key}
          data-testid={`theme-mode-${m.key}`}
          onClick={() => setThemeMode(m.key)}
          title={m.label}
          className={`relative z-10 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-colors ${
            themeMode === m.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground/70'
          }`}
        >
          {m.icon}
          <span className="hidden sm:inline">{m.label}</span>
        </button>
      ))}
    </div>
  );
}

function getGreeting(h) {
  if (h < 6) return 'Night sesh?';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

function ConditionsCard({ spots, conditionsCache, activeIndex, onPrev, onNext, loading, isTravel }) {
  const conditions = spots.length > 0 ? conditionsCache[spots[activeIndex]] : null;
  const spotLoading = loading || (spots.length > 0 && !conditions);

  if (!loading && spots.length === 0) {
    return (
      <div className="mx-4 mb-3 bg-bg-subtle/80 border border-white/5 rounded-2xl p-5 text-center" data-testid="no-spots-card">
        <MapPin className="w-5 h-5 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-xs font-medium text-muted-foreground/60 mb-1">
          {isTravel ? 'No spots nearby this location' : 'No favorite spots yet'}
        </p>
        <p className="text-[10px] text-muted-foreground/40">
          {isTravel ? 'Try searching for surf spots in this area' : 'Star spots from the Spots page to see them here'}
        </p>
      </div>
    );
  }

  if (spotLoading && !conditions) {
    return (
      <div className="mx-4 mb-3 bg-bg-subtle/80 border border-white/5 rounded-2xl p-4">
        <div className="flex items-center justify-center gap-2 py-4">
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot"></div>
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot"></div>
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot"></div>
        </div>
      </div>
    );
  }

  if (!conditions || conditions.error) return null;

  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < spots.length - 1;

  return (
    <div className="mx-4 mb-3 relative" data-testid="conditions-card">
      <button data-testid="conditions-prev" onClick={onPrev} disabled={!hasPrev}
        className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
          hasPrev ? 'bg-bg-paper/90 border border-white/10 hover:border-primary/40 hover:bg-white/5 shadow-lg' : 'opacity-0 pointer-events-none'
        }`}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button data-testid="conditions-next" onClick={onNext} disabled={!hasNext}
        className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
          hasNext ? 'bg-bg-paper/90 border border-white/10 hover:border-primary/40 hover:bg-white/5 shadow-lg' : 'opacity-0 pointer-events-none'
        }`}>
        <ChevronRight className="w-4 h-4" />
      </button>

      <AnimatePresence mode="wait">
        <motion.div key={spots[activeIndex]}
          initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.2 }}
          className="bg-bg-subtle/80 border border-white/5 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Live conditions</p>
              <p className="text-sm font-medium mt-0.5">{conditions.spot}</p>
            </div>
            <span className="text-xs text-primary font-medium bg-primary/10 border border-primary/20 rounded-full px-2.5 py-1">
              {conditions.summary}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center" data-testid="wave-height">
              <Waves className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
              <p className="text-base font-bold leading-none">{conditions.wave_height_ft}'</p>
              <p className="text-[9px] text-muted-foreground mt-0.5">waves</p>
            </div>
            <div className="text-center" data-testid="swell-info">
              <ArrowUp className="w-3.5 h-3.5 text-secondary mx-auto mb-1" />
              <p className="text-base font-bold leading-none">{conditions.swell_period_s}s</p>
              <p className="text-[9px] text-muted-foreground mt-0.5">{conditions.swell_direction} swell</p>
            </div>
            <div className="text-center" data-testid="wind-info">
              <Wind className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-1" />
              <p className="text-base font-bold leading-none">{Math.round(conditions.wind_speed_kph)}</p>
              <p className="text-[9px] text-muted-foreground mt-0.5">{conditions.wind_direction} kph</p>
            </div>
            <div className="text-center" data-testid="temp-info">
              <Thermometer className="w-3.5 h-3.5 text-accent mx-auto mb-1" />
              <p className="text-base font-bold leading-none">{conditions.air_temp_f != null ? Math.round(conditions.air_temp_f) : '--'}</p>
              <p className="text-[9px] text-muted-foreground mt-0.5">air &deg;F</p>
            </div>
          </div>
          {spots.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              {spots.map((_, i) => (
                <div key={i} className={`rounded-full transition-all ${i === activeIndex ? 'w-4 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-white/15'}`} />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function Home({ user, onLogout }) {
  const navigate = useNavigate();
  const { isPro } = useSubscription();
  const { localTime, themeMode, setThemeMode, locationOverride, updateLocationOverride, isDark, getEffectiveHour } = useTheme();
  const [nearbySpots, setNearbySpots] = useState([]);
  const [conditionsCache, setConditionsCache] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [conditionsLoading, setConditionsLoading] = useState(true);

  // Location picker state
  const [showLocPicker, setShowLocPicker] = useState(false);
  const [locInput, setLocInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [surfChips, setSurfChips] = useState([]);
  const searchTimer = useRef(null);
  const locRef = useRef(null);
  const [showVibeCheck, setShowVibeCheck] = useState(false);
  const [showStreakCard, setShowStreakCard] = useState(false);
  const [showStreakRescue, setShowStreakRescue] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [upcomingTrip, setUpcomingTrip] = useState(null);

  // Auto-show streak milestone card
  useEffect(() => {
    if (user?.login_streak && shouldShowMilestone(user.login_streak)) {
      const timer = setTimeout(() => setShowStreakCard(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user?.login_streak]);

  // Auto-show streak rescue offer
  useEffect(() => {
    if (user?.streak_rescue_available && user?.rescuable_streak >= 3) {
      const timer = setTimeout(() => setShowStreakRescue(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user?.streak_rescue_available, user?.rescuable_streak]);

  // Load personalized chips + upcoming trip alert + unread notif count
  useEffect(() => {
    authFetch(`${API}/api/top-destinations`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.destinations) setSurfChips(d.destinations); })
      .catch(() => {});
    authFetch(`${API}/api/trips/upcoming-alert`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.trip) setUpcomingTrip(d.trip); })
      .catch(() => {});
    authFetch(`${API}/api/notifications`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUnreadCount(d.unread || 0); })
      .catch(() => {});
  }, []);

  const fetchConditionsForSpot = useCallback(async (spotName) => {
    if (conditionsCache[spotName]) return;
    try {
      let res = await authFetch(`${API}/api/windy/forecast?spot=${encodeURIComponent(spotName)}`);
      if (res.ok) {
        const data = await res.json();
        const c = data.current || {};
        setConditionsCache(prev => ({ ...prev, [spotName]: {
          spot: data.spot || spotName, summary: data.summary, source: 'windy',
          wave_height_ft: c.wave_height_ft || 0, swell_height_ft: c.swell_height_ft || 0,
          swell_period_s: c.swell_period_s || 0, swell_direction: c.swell_direction || '',
          wind_speed_kph: c.wind_speed_kph || 0, wind_direction: c.wind_direction || '',
          air_temp_f: c.air_temp_f || c.temperature_f || null,
        }}));
        return;
      }
      res = await authFetch(`${API}/api/conditions?spot=${encodeURIComponent(spotName)}`);
      if (res.ok) {
        const data = await res.json();
        setConditionsCache(prev => ({ ...prev, [spotName]: data }));
      }
    } catch {}
  }, [conditionsCache]);

  useEffect(() => {
    const init = async () => {
      try {
        const url = locationOverride
          ? `${API}/api/nearby-spots?city=${encodeURIComponent(locationOverride.city)}`
          : `${API}/api/nearby-spots`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          const spots = data.spots || [];
          setNearbySpots(spots);
          if (spots.length > 0) await fetchConditionsForSpot(spots[0]);
        }
      } catch {} finally { setConditionsLoading(false); }
    };
    init();
  // eslint-disable-next-line
  }, [locationOverride]);

  const handlePrev = () => { const i = Math.max(0, activeIndex - 1); setActiveIndex(i); fetchConditionsForSpot(nearbySpots[i]); };
  const handleNext = () => { const i = Math.min(nearbySpots.length - 1, activeIndex + 1); setActiveIndex(i); fetchConditionsForSpot(nearbySpots[i]); };

  useEffect(() => {
    if (nearbySpots.length > activeIndex + 1) fetchConditionsForSpot(nearbySpots[activeIndex + 1]);
  // eslint-disable-next-line
  }, [activeIndex, nearbySpots]);

  const currentConditions = nearbySpots.length > 0 ? conditionsCache[nearbySpots[activeIndex]] : null;

  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const handleLocInputChange = (val) => {
    setLocInput(val);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setSuggestions([]); return; }
    setSugLoading(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await authFetch(`${API}/api/city-search?q=${encodeURIComponent(val.trim())}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.results || []);
        }
      } catch {} finally { setSugLoading(false); }
    }, 350);
  };

  const handleSuggestionSelect = async (sug) => {
    setShowLocPicker(false);
    setSuggestions([]);
    setLocInput('');
    // Record pick for personalization
    authFetch(`${API}/api/location-pick`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },       body: JSON.stringify({ city: sug.city, label: sug.label || sug.city }),
    }).catch(() => {});
    await updateLocationOverride({ city: sug.city });
    setConditionsCache({});
    setActiveIndex(0);
  };

  const handleLocationClear = async () => {
    await updateLocationOverride(null);
    setConditionsCache({});
    setActiveIndex(0);
    setShowLocPicker(false);
    setLocInput('');
    setSuggestions([]);
  };

  useEffect(() => {
    if (showLocPicker && locRef.current) locRef.current.focus();
  }, [showLocPicker]);

  const mainGrid = [
    { icon: MessageCircle, label: 'Chat', path: '/chat' },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  ];

  const secondaryGrid = [
    { icon: ClipboardList, label: 'Sessions', path: '/sessions' },
    { icon: MapPin, label: 'Spots', path: '/spots' },
    { icon: Users, label: 'Crew', path: '/crew' },
    { icon: CalendarDays, label: 'Calendar', path: '/calendar' },
    { icon: Warehouse, label: 'Garage', path: '/garage' },
    { icon: Trophy, label: 'Community', path: '/community' },
  ];

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-8 md:py-12 relative z-10">
      <SEO title="Home" description="Your SurfSwell.ai dashboard — check live conditions, track your streak, and plan your next session." path="/home" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md" data-testid="home-page">
        <div className="bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl relative">

          {/* Top Nav — Swell icon (notifications) + Date/Time + Controls + Avatar */}
          <div className="flex items-center gap-2.5 px-5 py-3.5 relative">
            <button
              data-testid="swell-notif-btn"
              onClick={() => setShowNotifs(!showNotifs)}
              className="relative p-0.5 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
            >
              <Waves className="w-5 h-5 text-primary" />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center"
                  data-testid="notif-badge"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight truncate">{dateStr}</p>
              <p className="text-[10px] text-muted-foreground leading-tight">{localTime}{locationOverride ? ` \u00B7 ${locationOverride.city}` : ''}</p>
            </div>

            {/* Theme mode toggle — Light / Dynamic / Dark */}
            <ThemeToggle themeMode={themeMode} setThemeMode={setThemeMode} />

            {/* Location override */}
            <button
              data-testid="location-override-btn"
              onClick={() => setShowLocPicker(!showLocPicker)}
              title={locationOverride ? locationOverride.city : 'Set location'}
              className={`flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${locationOverride ? 'bg-primary/15 text-primary' : 'hover:bg-white/10 text-muted-foreground'}`}
            >
              <Plane className="w-3.5 h-3.5" />
              <span className="text-[10px] font-medium">Travel</span>
            </button>

            {/* Avatar */}
            <button data-testid="home-avatar-btn" onClick={() => navigate('/profile')}
              className={`w-8 h-8 rounded-full overflow-hidden flex-shrink-0 transition-colors ${isPro ? 'ring-2 ring-amber-400/70 shadow-[0_0_8px_rgba(251,191,36,0.3)]' : 'border-2 border-white/10 hover:border-primary/50'}`}>
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center text-xs font-bold">
                  {(user?.name || '?')[0].toUpperCase()}
                </div>
              )}
            </button>
          </div>

          {/* Location Picker Dropdown */}
          <AnimatePresence>
            {showLocPicker && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="px-5 pb-3" style={{ overflow: 'visible' }}>
                <div className="bg-bg-subtle/80 border border-white/5 rounded-xl p-3" data-testid="location-picker">
                  <p className="text-[10px] text-muted-foreground mb-2">Set location for weather & conditions</p>
                  {/* Quick-select surf destinations */}
                  <div className="flex flex-wrap gap-1.5 mb-2.5" data-testid="quick-surf-chips">
                    {surfChips.map(spot => (
                      <button
                        key={spot.city}
                        data-testid={`quick-chip-${spot.city.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        onClick={() => handleSuggestionSelect(spot)}
                        className="text-[9px] font-medium px-2 py-1 rounded-full bg-primary/[0.08] text-primary/80 border border-primary/10 hover:bg-primary/20 hover:text-primary transition-colors"
                      >
                        {spot.city}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      ref={locRef}
                      data-testid="location-input"
                      type="text"
                      value={locInput}
                      onChange={e => handleLocInputChange(e.target.value)}
                      placeholder="Search a city..."
                      autoComplete="off"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
                    />
                    {sugLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-3 h-3 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                      </div>
                    )}
                    {/* Suggestions Dropdown */}
                    {suggestions.length > 0 && (
                      <div className="absolute inset-x-0 top-full mt-1 bg-bg-paper border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="location-suggestions">
                        {suggestions.map((sug, i) => (
                          <button
                            key={i}
                            data-testid={`location-suggestion-${i}`}
                            onClick={() => handleSuggestionSelect(sug)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-primary/10 transition-colors border-b border-white/[0.04] last:border-0 flex items-center gap-2"
                          >
                            <MapPin className={`w-3 h-3 flex-shrink-0 ${sug.coastal ? 'text-primary' : 'text-muted-foreground/40'}`} />
                            <span className="truncate flex-1">{sug.label}</span>
                            {sug.coastal && (
                              <span className="text-[8px] text-primary/70 bg-primary/10 rounded-full px-1.5 py-0.5 flex-shrink-0">surf</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {locInput.trim().length >= 2 && !sugLoading && suggestions.length === 0 && (
                      <div className="absolute inset-x-0 top-full mt-1 bg-bg-paper border border-white/10 rounded-lg shadow-xl z-50 px-3 py-2">
                        <p className="text-[10px] text-muted-foreground/50">No cities found</p>
                      </div>
                    )}
                  </div>
                  {locationOverride && (
                    <button onClick={handleLocationClear} data-testid="location-clear-btn"
                      className="flex items-center gap-1 text-[10px] text-accent mt-2 hover:underline">
                      <X className="w-2.5 h-2.5" />
                      Clear — use my location
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Upcoming Trip Alert */}
          {upcomingTrip && (
            <div className="px-3 pt-1">
              <button
                onClick={() => navigate('/calendar')}
                className="w-full bg-secondary/[0.08] border border-secondary/20 rounded-2xl px-4 py-2.5 flex items-center gap-3 hover:bg-secondary/[0.12] transition-colors"
                data-testid="upcoming-trip-alert"
              >
                <Plane className="w-4 h-4 text-secondary flex-shrink-0" />
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-semibold truncate">{upcomingTrip.destination}</p>
                  <p className="text-[9px] text-secondary/70">
                    {upcomingTrip.days_until === 0 ? 'Today!' : upcomingTrip.days_until === 1 ? 'Tomorrow!' : `${upcomingTrip.days_until} days away`}
                  </p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-secondary/40 flex-shrink-0" />
              </button>
            </div>
          )}

          {/* Dynamic Beach Scene */}
          <div className="px-3 pt-1">
            <BeachScene />
          </div>

          {/* Greeting */}
          <div className="text-center px-5 pt-1 pb-2">
            <h1 className="font-heading text-2xl font-semibold" data-testid="home-greeting">
              {getGreeting(getEffectiveHour())}, {user?.name?.split(' ')[0] || 'surfer'}
            </h1>
            {currentConditions && !currentConditions.error ? (
              <p className="text-sm text-muted-foreground mt-1">
                {currentConditions.wave_height_ft}' {currentConditions.summary} at {currentConditions.spot}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">ready to shred?</p>
            )}
          </div>

          {/* Live Conditions */}
          <ConditionsCard spots={nearbySpots} conditionsCache={conditionsCache} activeIndex={activeIndex}
            onPrev={handlePrev} onNext={handleNext} loading={conditionsLoading} isTravel={!!locationOverride} />

          {/* Vibe Check Button */}
          {currentConditions && !currentConditions.error && (
            <div className="px-5 pb-3">
              <button
                data-testid="vibe-check-btn"
                onClick={() => setShowVibeCheck(true)}
                className="w-full group bg-bg-subtle/60 hover:bg-bg-subtle/80 border border-white/[0.06] hover:border-white/[0.12] rounded-2xl py-3 px-4 flex items-center justify-center gap-2.5 transition-all duration-300"
              >
                <Sparkles className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">
                  Vibe Check
                </span>
              </button>
            </div>
          )}

          {/* Main Grid */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-3">
              {mainGrid.map(item => (
                <button key={item.label} data-testid={`home-btn-${item.label.toLowerCase()}`} onClick={() => navigate(item.path)}
                  className="bg-bg-subtle/80 hover:bg-bg-subtle border border-white/5 hover:border-primary/30 rounded-2xl p-5 flex flex-col items-center gap-2.5 transition-all duration-200">
                  <item.icon className="w-6 h-6 text-muted-foreground" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Secondary Grid */}
          <div className="px-5 pb-5">
            <div className="grid grid-cols-3 gap-3">
              {secondaryGrid.map(item => (
                <button key={item.label} data-testid={`home-btn-${item.label.toLowerCase()}`} onClick={() => navigate(item.path)}
                  className="bg-bg-subtle/80 hover:bg-bg-subtle border border-white/5 hover:border-primary/30 rounded-2xl p-4 flex flex-col items-center gap-2 transition-all duration-200">
                  <item.icon className="w-5 h-5 text-muted-foreground" />
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="px-5 pb-8"></div>
          <BetaBadge />

          {/* Notifications Panel — overlays from top of card */}
          <NotificationsPanel
            open={showNotifs}
            onClose={() => { setShowNotifs(false); setUnreadCount(0); }}
          />
        </div>
      </motion.div>

      {/* Vibe Check Modal */}
      <AnimatePresence>
        {showVibeCheck && currentConditions && (
          <VibeCheck
            conditions={currentConditions}
            spotName={currentConditions.spot || nearbySpots[activeIndex] || ''}
            onClose={() => setShowVibeCheck(false)}
            user={user}
          />
        )}
      </AnimatePresence>

      {/* Streak Milestone Modal */}
      <AnimatePresence>
        {showStreakCard && (
          <StreakCard
            user={user}
            onClose={() => setShowStreakCard(false)}
          />
        )}
      </AnimatePresence>

      {/* Streak Rescue Modal */}
      <AnimatePresence>
        {showStreakRescue && (
          <StreakRescue
            user={user}
            onClose={() => setShowStreakRescue(false)}
            onRescued={(data) => {
              setShowStreakRescue(false);
              window.location.reload();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
